/**
 * HierarchicalView - Organized Timeline with Parent/Child Tasks
 * Displays tasks in hierarchical groups with progress tracking
 */

import React, { useState } from 'react';
import { Filter, Target, ListTodo } from 'lucide-react';
import { useHierarchicalTasks } from '../../hooks/useHierarchicalTasks';
import RecoveryModeBanner from './RecoveryModeBanner';
import NextActionCard from './NextActionCard';
import ProgressSummary from './ProgressSummary';
import SharedDependencyAlert from './SharedDependencyAlert';
import GroupedTaskCard from './GroupedTaskCard';
import { getNextAction } from '../../utils/taskUtils';

const HierarchicalView = ({
  timeline = [],
  colleges = [],
  onTaskClick,
  onTaskComplete,
  onAISupport,
  onFocusMode,
  className = ''
}) => {
  const [showCompleted, setShowCompleted] = useState(false);
  const [filterCategory, setFilterCategory] = useState('all');

  // Get hierarchical structure
  const {
    parentTasks,
    taskSubtasksMap,
    foundationTasks,
    isRecovery,
    allTasks
  } = useHierarchicalTasks(timeline);

  // Filter out completed parents if hidden
  const visibleParents = showCompleted
    ? parentTasks
    : parentTasks.filter(task => task.status !== 'completed');

  // Apply category filter
  const filteredParents = filterCategory === 'all'
    ? visibleParents
    : visibleParents.filter(task => task.category === filterCategory);

  // Get next action for ProgressSummary
  const nextAction = getNextAction(parentTasks, taskSubtasksMap);

  // Handle subtask completion
  const handleSubtaskComplete = async (subtask) => {
    if (onTaskComplete) {
      await onTaskComplete(subtask);
    }
  };

  // Categories for filter
  const categories = [
    { key: 'all', label: 'All Tasks', icon: '📋' },
    { key: 'essay', label: 'Essays', icon: '📝' },
    { key: 'application', label: 'Applications', icon: '📋' },
    { key: 'research', label: 'Research', icon: '🔍' },
    { key: 'testing', label: 'Testing', icon: '📊' },
    { key: 'profile', label: 'Profile', icon: '👤' }
  ];

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Recovery Mode Banner */}
      {isRecovery && (
        <RecoveryModeBanner
          tasks={parentTasks}
          colleges={colleges}
          onFocusClick={onFocusMode}
        />
      )}

      {/* Progress Summary Cards */}
      <ProgressSummary
        tasks={parentTasks}
        taskSubtasksMap={taskSubtasksMap}
        onNextActionClick={nextAction ? () => onTaskClick(nextAction.subtask) : undefined}
      />

      {/* Next Action Highlight (if not in recovery mode) */}
      {!isRecovery && nextAction && (
        <NextActionCard
          tasks={parentTasks}
          taskSubtasksMap={taskSubtasksMap}
          colleges={colleges}
          onStartClick={onTaskClick}
          onAISupportClick={onAISupport}
        />
      )}

      {/* Foundation Task Alerts */}
      {foundationTasks.length > 0 && (
        <div className="space-y-3">
          {foundationTasks.slice(0, 2).map(foundationTask => (
            <SharedDependencyAlert
              key={foundationTask.id}
              foundationTask={foundationTask}
              allTasks={allTasks}
              onTaskClick={onTaskClick}
            />
          ))}
        </div>
      )}

      {/* Filter Controls */}
      <div className="flex items-center justify-between gap-4">
        <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
          <ListTodo className="w-6 h-6 text-indigo-600" />
          Priority Tasks
        </h2>

        <div className="flex items-center gap-3">
          {/* Category Filter */}
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-500" />
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              {categories.map(cat => (
                <option key={cat.key} value={cat.key}>
                  {cat.icon} {cat.label}
                </option>
              ))}
            </select>
          </div>

          {/* Show Completed Toggle */}
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={showCompleted}
              onChange={(e) => setShowCompleted(e.target.checked)}
              className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
            />
            <span>Show completed</span>
          </label>
        </div>
      </div>

      {/* Task List */}
      {filteredParents.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-gray-500">
          <Target className="w-16 h-16 mb-4 text-gray-300" />
          <h3 className="text-lg font-medium mb-2">No tasks found</h3>
          <p className="text-sm">
            {filterCategory !== 'all'
              ? 'Try selecting a different category'
              : showCompleted
              ? 'All tasks are hidden'
              : 'Great work - all tasks are complete!'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredParents.map((parentTask, index) => {
            const subtasks = taskSubtasksMap[parentTask.id] || [];

            return (
              <GroupedTaskCard
                key={parentTask.id}
                parentTask={parentTask}
                subtasks={subtasks}
                index={index}
                onTaskClick={onTaskClick}
                onSubtaskComplete={handleSubtaskComplete}
                onAISupport={onAISupport}
              />
            );
          })}
        </div>
      )}

      {/* Task Count Footer */}
      {filteredParents.length > 0 && (
        <div className="text-center text-sm text-gray-600 pt-4 border-t border-gray-200">
          Showing {filteredParents.length} of {parentTasks.length} task group
          {parentTasks.length !== 1 ? 's' : ''}
        </div>
      )}
    </div>
  );
};

export default HierarchicalView;
